import type { DependencyList } from 'react';
import type { UseComponentKeyReturn, UseComponentOptions, UseComponentReturn } from '@ricoh/ria.client.core/hooks/app/component.type';

import { useCallback, useMemo, useRef, useState } from 'react';
import { useUpdateEffect } from '@ricoh/ria.client.core/hooks/app/lifecycle';
import { newUUID } from '@ricoh/ria.client.core/utils/data/key';

/**
 * Use Component ID. *
 *
 * @param {string} [prefix] Prefix.
 *
 * @returns {string} Component id.
 */
export function useComponentId(prefix?: string): string {
  const idRef = useRef<string>(newUUID());

  return useMemo<string>(() => (prefix ? `${prefix}-${idRef.current}` : idRef.current), [prefix]);
}

/**
 * Use Component Key.
 *
 * @param {DependencyList} deps Dependencies.
 *
 * @returns {UseComponentKeyReturn} Component.
 */
export function useComponentKey(deps: DependencyList = []): UseComponentKeyReturn {
  const [key, setKey] = useState<string>(newUUID(true));

  const childKey = useCallback<UseComponentKeyReturn['childKey']>((name, ...trailing) => `${key}-${name}-${trailing.join('-')}`, [key]);

  useUpdateEffect(() => {
    setKey(newUUID(true));

    // * Use `deps` params as DependencyList
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return useMemo<UseComponentKeyReturn>(
    () => ({
      key,
      childKey,
    }),
    [childKey, key],
  );
}

/**
 * Use Component.
 *
 * @template TClassName ClassName key type.
 * @param {UseComponentOptions<TClassName>} options Options.
 *
 * @returns {UseComponentReturn<TClassName>} Component.
 */
export function useComponent<TClassName extends string>({ classNames = [] }: UseComponentOptions<TClassName> = {}): UseComponentReturn<TClassName> {
  const componentId = useComponentId();

  const className = useMemo<UseComponentReturn<TClassName>['className']>(
    () =>
      classNames.reduce(
        (prev, classNameKey) => {
          prev[classNameKey] = `${classNameKey}_${componentId}`;

          return prev;
        },
        {} as UseComponentReturn<TClassName>['className'],
      ),
    [classNames, componentId],
  );

  return useMemo<UseComponentReturn<TClassName>>(
    () => ({
      className,
    }),
    [className],
  );
}
